import { auth, db, collection, doc, addDoc, updateDoc, deleteDoc, getDocs, getDoc, query, where, orderBy, serverTimestamp, setDoc } from "./firebase";

const COLLECTIONS = {
  WATCHED: "watchedMovies",
  VIEWINGS: "viewings",
  WATCHLIST: "watchlist",
  USERS: "users",
};

// ==================== WATCHED MOVIES ====================

export const addWatchedMovie = async (userId, movieData, viewing, friendsToSync = []) => {
  try {
    // Create the main movie document
    const docRef = await addDoc(collection(db, COLLECTIONS.WATCHED), {
      userId,
      tmdbId: movieData.tmdbId,
      title: movieData.title,
      posterPath: movieData.posterPath,
      releaseDate: movieData.releaseDate,
      genres: movieData.genres || [],
      runtime: movieData.runtime || 0,
      createdAt: serverTimestamp(),
    });

    // Add the first viewing
    await addDoc(collection(db, COLLECTIONS.VIEWINGS), {
      movieDocId: docRef.id,
      userId,
      tmdbId: movieData.tmdbId,
      date: viewing.date,
      rating: viewing.rating,
      notes: viewing.notes || "",
      watchedWith: viewing.watchedWith || [],
      createdAt: serverTimestamp(),
    });

    // Sync with friends - add movie to their watched list too
    for (const friend of friendsToSync) {
      await syncMovieWithFriend(friend.id, movieData, viewing, userId);
    }

    return docRef.id;
  } catch (error) {
    console.error("Error adding watched movie:", error);
    throw error;
  }
};

// Add a new viewing to an existing watched movie
export const addViewingToMovie = async (movieDocId, userId, tmdbId, viewing, friendsToSync = []) => {
  try {
    await addDoc(collection(db, COLLECTIONS.VIEWINGS), {
      movieDocId,
      userId,
      tmdbId,
      date: viewing.date,
      rating: viewing.rating,
      notes: viewing.notes || "",
      watchedWith: viewing.watchedWith || [],
      createdAt: serverTimestamp(),
    });

    // Sync with friends
    for (const friend of friendsToSync) {
      // Get movie data from the existing doc
      const movieDoc = await getDoc(doc(db, COLLECTIONS.WATCHED, movieDocId));
      if (movieDoc.exists()) {
        const movieData = movieDoc.data();
        await syncMovieWithFriend(friend.id, movieData, viewing, userId);
      }
    }
  } catch (error) {
    console.error("Error adding viewing:", error);
    throw error;
  }
};

// Sync movie to a friend's watched list
const syncMovieWithFriend = async (friendId, movieData, viewing, watchedWithUserId) => {
  try {
    // Get current user info for the watchedWith field
    const currentUser = auth.currentUser;
    const userInfo = { id: watchedWithUserId, name: currentUser?.displayName || currentUser?.email };

    // Check if friend already has this movie
    const existingMovie = await isMovieWatched(friendId, movieData.tmdbId);

    if (existingMovie) {
      // Add viewing to existing movie
      await addDoc(collection(db, COLLECTIONS.VIEWINGS), {
        movieDocId: existingMovie.id,
        userId: friendId,
        tmdbId: movieData.tmdbId,
        date: viewing.date,
        rating: 0, // Friend can rate it themselves
        notes: "",
        watchedWith: [userInfo],
        addedBy: watchedWithUserId,
        createdAt: serverTimestamp(),
      });
    } else {
      // Create new watched movie for friend
      const friendMovieDoc = await addDoc(collection(db, COLLECTIONS.WATCHED), {
        userId: friendId,
        tmdbId: movieData.tmdbId,
        title: movieData.title,
        posterPath: movieData.posterPath,
        releaseDate: movieData.releaseDate,
        genres: movieData.genres || [],
        runtime: movieData.runtime || 0,
        createdAt: serverTimestamp(),
      });

      // Add viewing for friend
      await addDoc(collection(db, COLLECTIONS.VIEWINGS), {
        movieDocId: friendMovieDoc.id,
        userId: friendId,
        tmdbId: movieData.tmdbId,
        date: viewing.date,
        rating: 0,
        notes: "",
        watchedWith: [userInfo],
        addedBy: watchedWithUserId,
        createdAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error("Error syncing movie with friend:", error);
    // Don't throw - we don't want to fail the whole operation if sync fails
  }
};

// Get all viewings for a specific movie
export const getMovieViewings = async (userId, tmdbId) => {
  try {
    const q = query(collection(db, COLLECTIONS.VIEWINGS), where("userId", "==", userId), where("tmdbId", "==", tmdbId), orderBy("date", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting movie viewings:", error);
    return [];
  }
};

// Delete a single viewing
export const deleteViewing = async (viewingId, movieDocId) => {
  try {
    // Delete the viewing
    await deleteDoc(doc(db, COLLECTIONS.VIEWINGS, viewingId));

    // Check if there are any remaining viewings for this movie
    const remainingViewings = query(collection(db, COLLECTIONS.VIEWINGS), where("movieDocId", "==", movieDocId));
    const snapshot = await getDocs(remainingViewings);

    // If no more viewings, delete the watched movie document too
    if (snapshot.empty) {
      await deleteDoc(doc(db, COLLECTIONS.WATCHED, movieDocId));
      return { movieDeleted: true };
    }

    return { movieDeleted: false };
  } catch (error) {
    console.error("Error deleting viewing:", error);
    throw error;
  }
};

export const getWatchedMovies = async (userId) => {
  try {
    const q = query(collection(db, COLLECTIONS.WATCHED), where("userId", "==", userId), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting watched movies:", error);
    throw error;
  }
};

export const updateWatchedMovie = async (movieId, updates) => {
  try {
    const docRef = doc(db, COLLECTIONS.WATCHED, movieId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating watched movie:", error);
    throw error;
  }
};

export const deleteWatchedMovie = async (movieId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.WATCHED, movieId));
    // Also delete all viewings
    const viewingsQuery = query(collection(db, COLLECTIONS.VIEWINGS), where("movieDocId", "==", movieId));
    const viewingsSnapshot = await getDocs(viewingsQuery);
    for (const viewingDoc of viewingsSnapshot.docs) {
      await deleteDoc(doc(db, COLLECTIONS.VIEWINGS, viewingDoc.id));
    }
  } catch (error) {
    console.error("Error deleting watched movie:", error);
    throw error;
  }
};

export const isMovieWatched = async (userId, tmdbId) => {
  try {
    const q = query(collection(db, COLLECTIONS.WATCHED), where("userId", "==", userId), where("tmdbId", "==", tmdbId));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    return null;
  } catch (error) {
    console.error("Error checking if movie watched:", error);
    return null;
  }
};

// ==================== WATCHLIST ====================

export const addToWatchlist = async (userId, movieData, friendsToSync = []) => {
  try {
    const currentUser = auth.currentUser;
    const existing = await isInWatchlist(userId, movieData.tmdbId);
    if (existing) {
      throw new Error("Movie already in watchlist");
    }

    // Prepare watchWith array if there are friends
    const watchWith = friendsToSync.map((f) => ({
      id: f.id,
      name: f.name,
    }));

    const docRef = await addDoc(collection(db, COLLECTIONS.WATCHLIST), {
      userId,
      tmdbId: movieData.tmdbId,
      title: movieData.title,
      posterPath: movieData.posterPath,
      releaseDate: movieData.releaseDate,
      genres: movieData.genres || [],
      addedAt: serverTimestamp(),
      priority: movieData.priority || 0,
      watchWith: watchWith, // Friends to watch with
      recommendedBy: null, // Not recommended, added by self
    });

    // Sync to friends' watchlists with recommendation tag
    for (const friend of friendsToSync) {
      await syncWatchlistWithFriend(friend.id, movieData, {
        id: userId,
        name: currentUser?.displayName || currentUser?.email,
      });
    }

    return docRef.id;
  } catch (error) {
    console.error("Error adding to watchlist:", error);
    throw error;
  }
};

// Sync watchlist movie to a friend
const syncWatchlistWithFriend = async (friendId, movieData, recommendedBy) => {
  try {
    // Check if friend already has this in watchlist
    const existing = await isInWatchlist(friendId, movieData.tmdbId);
    if (existing) {
      // Movie already in their watchlist, don't duplicate
      return;
    }

    await addDoc(collection(db, COLLECTIONS.WATCHLIST), {
      userId: friendId,
      tmdbId: movieData.tmdbId,
      title: movieData.title,
      posterPath: movieData.posterPath,
      releaseDate: movieData.releaseDate,
      genres: movieData.genres || [],
      addedAt: serverTimestamp(),
      priority: 0,
      watchWith: [recommendedBy], // Suggested to watch with the person who recommended
      recommendedBy: recommendedBy, // Who recommended this movie
    });
  } catch (error) {
    console.error("Error syncing watchlist with friend:", error);
  }
};

export const getWatchlist = async (userId) => {
  try {
    const q = query(collection(db, COLLECTIONS.WATCHLIST), where("userId", "==", userId), orderBy("addedAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error getting watchlist:", error);
    throw error;
  }
};

export const removeFromWatchlist = async (watchlistItemId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.WATCHLIST, watchlistItemId));
  } catch (error) {
    console.error("Error removing from watchlist:", error);
    throw error;
  }
};

export const isInWatchlist = async (userId, tmdbId) => {
  try {
    const q = query(collection(db, COLLECTIONS.WATCHLIST), where("userId", "==", userId), where("tmdbId", "==", tmdbId));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
    }
    return null;
  } catch (error) {
    console.error("Error checking watchlist:", error);
    return null;
  }
};

// ==================== USER PROFILE ====================

export const getUserProfile = async (userId) => {
  try {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error("Error getting user profile:", error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updates) => {
  try {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating user profile:", error);
    throw error;
  }
};

// ==================== SHARING ====================

export const getPublicHistory = async (userId) => {
  try {
    const userProfile = await getUserProfile(userId);
    if (!userProfile?.isHistoryPublic) {
      throw new Error("History is private");
    }

    return await getWatchedMovies(userId);
  } catch (error) {
    console.error("Error getting public history:", error);
    throw error;
  }
};

export const toggleHistoryVisibility = async (userId, isPublic) => {
  try {
    await updateUserProfile(userId, { isHistoryPublic: isPublic });
  } catch (error) {
    console.error("Error toggling history visibility:", error);
    throw error;
  }
};

// ==================== STATS ====================

export const getUserStats = async (userId) => {
  try {
    const watchedMovies = await getWatchedMovies(userId);

    const totalMovies = watchedMovies.length;
    const totalMinutes = watchedMovies.reduce((acc, m) => acc + (m.runtime || 0), 0);

    // Get all viewings for rating calculation
    const viewingsQuery = query(collection(db, COLLECTIONS.VIEWINGS), where("userId", "==", userId));
    const viewingsSnapshot = await getDocs(viewingsQuery);
    const allViewings = viewingsSnapshot.docs.map((d) => d.data());
    const ratingsWithValues = allViewings.filter((v) => v.rating > 0);
    const averageRating = ratingsWithValues.length > 0 ? ratingsWithValues.reduce((acc, v) => acc + v.rating, 0) / ratingsWithValues.length : 0;

    // Genre distribution
    const genreCount = {};
    watchedMovies.forEach((movie) => {
      (movie.genres || []).forEach((genre) => {
        genreCount[genre] = (genreCount[genre] || 0) + 1;
      });
    });

    // Monthly distribution from viewings
    const monthlyCount = {};
    allViewings.forEach((viewing) => {
      if (viewing.date) {
        const month = viewing.date.substring(0, 7);
        monthlyCount[month] = (monthlyCount[month] || 0) + 1;
      }
    });

    return {
      totalMovies,
      totalMinutes,
      totalHours: Math.round(totalMinutes / 60),
      averageRating: Math.round(averageRating * 10) / 10,
      genreDistribution: genreCount,
      monthlyDistribution: monthlyCount,
      totalViewings: allViewings.length,
    };
  } catch (error) {
    console.error("Error getting user stats:", error);
    throw error;
  }
};
